import { BaseEnumClass } from '@/types/base.class';
import { SOActionCode, SOAllocateStatusCode, SOEmailAction, SOEmailEventTypeCode, SOEmailStatusCode, SOStatusCode } from '@/types/so';
import { Department } from '@/types/user';

/**
 * 用类定义的方式来声明订舱状态枚举
 *
 * @version 1.0.0
 */
export class BookingStatus extends BaseEnumClass<BookingStatus> {
  constructor(
    public readonly code: string,
    public readonly category: string,
    public readonly title: string,
    public readonly color: string,
    public readonly actions: string[]
  ) { super(); }
  // 枚举值定义
  static readonly NEW_CREATED = new BookingStatus("00", "检验阶段，待检验", "待处理", "processing", ["AMEND", "CANCEL", "MERGE"]);
  static readonly VERIFIED_CORRECT = new BookingStatus("01", "检验阶段，检验后", "待处理", "processing", ["AMEND", "CANCEL", "MERGE"]);
  static readonly VERIFIED_NEED_FIX = new BookingStatus("02", "检验阶段，检验后", "检验不通过", "default", ["AMEND", "CANCEL", "MERGE"]);
  static readonly ABNORMAL_PENDING = new BookingStatus("03", "订单变动，待处理", "待处理变动", "default", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_MODIFIED = new BookingStatus("04", "订单变动，处理后", "已修改", "geekblue", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_CANCELLED = new BookingStatus("05", "订单变动，处理后", "已取消", "default", ["RECOVER"]);
  static readonly PROCESSED_MERGED = new BookingStatus("06", "订单变动，处理后", "已合并", "default", []);
  static readonly PROCESSED_MARKED = new BookingStatus("07", "订单变动，处理后", "已放舱", "success", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_MODIFIED_AFTER_MARKED = new BookingStatus("08", "订单变动，处理后", "需托单变更", "geekblue", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_NO_CONT = new BookingStatus("09", "订单变动，处理后", "无舱位", "warning", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_IN_QUEUE = new BookingStatus("10", "订单变动，处理后", "需排队", "orange", ["AMEND", "CANCEL", "MERGE"]);
  static readonly PROCESSED_EXPIRED = new BookingStatus("11", "订单变动，处理后", "已过期", "default", []);
  static readonly PROCESSED_PENDING = new BookingStatus("12", "订单变动，处理后", "待放舱", "default", ["AMEND", "CANCEL",]);
  static readonly VERIFICATION_NEED_REVIEW = new BookingStatus("98", "检验阶段，待检验", "待处理", "processing", ["AMEND", "CANCEL", "MERGE"]);
  static readonly VERIFICATION_NEED_REVIEW_WARNING = new BookingStatus("99", "检验阶段", "待处理异常", "error", ["AMEND", "CANCEL", "MERGE"]);

  /** 允许批量更改分组的托单状态（早期状态） */
  static readonly ALLOWED_FOR_CHANGE_PORT_GROUP: BookingStatus[] = [
    BookingStatus.NEW_CREATED,
    BookingStatus.VERIFIED_CORRECT,
    BookingStatus.VERIFIED_NEED_FIX,
    BookingStatus.ABNORMAL_PENDING,
    BookingStatus.PROCESSED_MODIFIED,
    BookingStatus.PROCESSED_CANCELLED,
    BookingStatus.PROCESSED_MERGED,
    BookingStatus.PROCESSED_MARKED,
    BookingStatus.PROCESSED_MODIFIED_AFTER_MARKED,
    BookingStatus.PROCESSED_NO_CONT,
    BookingStatus.PROCESSED_IN_QUEUE,
    BookingStatus.PROCESSED_EXPIRED,
    BookingStatus.PROCESSED_PENDING,
    BookingStatus.VERIFICATION_NEED_REVIEW,
    BookingStatus.VERIFICATION_NEED_REVIEW_WARNING,
  ];

  static getBookingStatusChangeDesc = (oldStatus: string, newStatus: string) => {
    const oldStatusTitle = BookingStatus.fromCode<BookingStatus>(oldStatus)?.title;
    const newStatusTitle = BookingStatus.fromCode<BookingStatus>(newStatus)?.title;
    return `从状态[${oldStatusTitle}]变更为状态[${newStatusTitle}]`
  };
}

/**
 * 用类定义的方式来声明订舱操作枚举
 *
 * @version 1.0.0
 */
export class BookingProcess extends BaseEnumClass<BookingProcess> {
  constructor(
    public readonly code: string,
    public readonly title: string,
    public readonly status: string,
    // 允许执行当前操作的状态列表，默认为空数组，表示允许所有状态
    public readonly allowedStatuses: BookingStatus[] = [],
    // 不允许执行当前操作的状态列表，默认为空数组，表示没有设置不允许状态，即不进行限制
    public readonly disallowedStatuses: BookingStatus[] = []
  ) { super(); }

  /**
   * 检查某个状态是否允许执行当前操作
   * @param status 要检查的状态
   * @returns 是否允许执行
   */
  canExecute(status: string): boolean {
    const statusObj = BookingStatus.fromCode<BookingStatus>(status);
    if (!statusObj) return false;

    // 如果状态在不允许列表中，直接返回false
    if (this.disallowedStatuses.includes(statusObj)) {
      return false;
    }

    // 如果允许列表为空，默认允许执行
    if (this.allowedStatuses.length === 0) {
      return true;
    }

    // 检查状态是否在允许列表中
    return this.allowedStatuses.includes(statusObj);
  }

  // 枚举值定义
  static readonly CREATE = new BookingProcess("CREATE", "新建订舱", "00");
  static readonly VERIFY_PASSED = new BookingProcess("VERIFY_PASSED", "已通过检验", "01");
  static readonly VERIFY_REJECT = new BookingProcess("VERIFY_REJECT", "检验不通过", "02");
  static readonly AMEND = new BookingProcess("AMEND", "修改订舱", "04");
  static readonly CANCEL = new BookingProcess("CANCEL", "取消订舱", "05", [], [BookingStatus.PROCESSED_CANCELLED]);
  static readonly RECOVER = new BookingProcess("RECOVER", "复原订舱", "pre-cancel-status");
  static readonly MERGE = new BookingProcess("MERGE", "合并订舱", "06");
  static readonly MARK = new BookingProcess("MARK", "标记为已放舱", "07");
  static readonly MARK_NO_CONT = new BookingProcess("MARK_NO_CONT", "标记为无舱位", "09");
  static readonly MARK_IN_QUEUE = new BookingProcess("MARK_IN_QUEUE", "标记为需排队", "10");
  static readonly APPROVE_PASS = new BookingProcess("APPROVE_PASS", "可以放舱", "07");
  static readonly APPROVE_PENDING = new BookingProcess("APPROVE_PENDING", "放舱待定", "no-status-change");
  static readonly APPROVE_REJECT = new BookingProcess("APPROVE_REJECT", "不能放舱", "no-status-change");
  static readonly MARK_EXPIRED = new BookingProcess("MARK_EXPIRED", "标记为已过期", "11");
  static readonly MARK_PENDING = new BookingProcess("MARK_PENDING", "标记为待放舱", "12");
  static readonly ALLOCATE = new BookingProcess("ALLOCATE", "放舱", "12", [], [BookingStatus.PROCESSED_EXPIRED, BookingStatus.PROCESSED_CANCELLED]);
  /**
   * 批量更改港口组：业务重新分配动作，不触发 booking.status 变更。
   * status 使用特殊值 "no-status-change"，由批量改分组服务识别并保持原 status 不变。
   */
  static readonly CHANGE_PORT_GROUP = new BookingProcess("CHANGE_PORT_GROUP", "更改分组", "no-status-change");
}


/**
 * SO 状态枚举（船东谈判态；放舱态见 SOAllocateStatus）
 */
export class SOStatus extends BaseEnumClass<SOStatus> {
  constructor(
    public readonly code: SOStatusCode,
    public readonly category: string,
    public readonly title: string,
    public readonly color: string,
    public readonly actions: SOActionCode[]
  ) { super(); }

  static readonly ENTERED = new SOStatus('ENTERED', '订舱已登记', '订舱已登记', 'blue', ['VIEW', 'MODIFY', 'CONFIRM', 'CANCEL']);
  static readonly ACKED = new SOStatus('ACKED', '订舱已受理', '订舱已受理', 'purple', ['VIEW', 'MODIFY', 'CONFIRM', 'CANCEL']);
  static readonly CONFIRMED = new SOStatus('CONFIRMED', '已成功订舱', '已成功订舱', 'green', ['VIEW', 'MODIFY', 'CANCEL', 'PUBLISH', 'UNPUBLISH']);
  static readonly MODIFIED = new SOStatus('MODIFIED', '已修改订舱', '已修改订舱', 'orange', ['VIEW', 'MODIFY', 'CANCEL', 'PUBLISH', 'UNPUBLISH']);
  static readonly CANCELLED = new SOStatus('CANCELLED', '已取消订舱', '已取消订舱', 'red', ['VIEW', 'RECOVER']);
  static readonly EXPIRED = new SOStatus('EXPIRED', '已过期', '已过期', 'default', ['VIEW']);

  /**
   * 检查船东状态能否被人工设置为目标值（EXPIRED 为系统终态，不可指定）
   */
  static canSetTo(code: string): boolean {
    const status = SOStatus.fromCode<SOStatus>(code);
    return !!status && status.code !== 'EXPIRED';
  }
}

/**
 * SO 放舱状态枚举（内部放舱生命周期，与船东 status 解耦）
 */
export class SOAllocateStatus extends BaseEnumClass<SOAllocateStatus> {
  constructor(
    public readonly code: SOAllocateStatusCode,
    public readonly title: string,
    public readonly color: string
  ) { super(); }

  static readonly AVAILABLE = new SOAllocateStatus('AVAILABLE', '空闲舱', 'green');
  static readonly SUSPENDED = new SOAllocateStatus('SUSPENDED', '已挂起', 'gold');
  static readonly ALLOCATED = new SOAllocateStatus('ALLOCATED', '已放舱', 'geekblue');
  static readonly CANCELLED = new SOAllocateStatus('CANCELLED', '已取消', 'default');
}

/**
 * SO 放舱操作枚举（ALLOCATE/SUSPENDED/RELEASE，gate 在 allocate_status）
 */
export class SOAllocateAction extends BaseEnumClass<SOAllocateAction> {
  constructor(
    public readonly code: SOActionCode,
    public readonly title: string,
    public readonly targetAllocateStatus: SOAllocateStatusCode | '' = '',
    public readonly allowedAllocateStatuses: SOAllocateStatus[] = [],
    public readonly disallowedAllocateStatuses: SOAllocateStatus[] = []
  ) { super(); }

  /**
   * 检查某个放舱状态是否允许执行当前放舱操作
   */
  canExecute(allocateStatus: string): boolean {
    const statusObj = SOAllocateStatus.fromCode<SOAllocateStatus>(allocateStatus);
    if (!statusObj) return false;
    if (this.disallowedAllocateStatuses.includes(statusObj)) return false;
    if (this.allowedAllocateStatuses.length === 0) return true;
    return this.allowedAllocateStatuses.includes(statusObj);
  }

  /** 直配（无 booking 的 legacy 路径）：AVAILABLE → ALLOCATED */
  static readonly ALLOCATE = new SOAllocateAction('ALLOCATE', '放舱', 'ALLOCATED', [SOAllocateStatus.AVAILABLE], [SOAllocateStatus.CANCELLED]);
  /** 挂起：ALLOCATED → SUSPENDED */
  static readonly SUSPEND = new SOAllocateAction('SUSPEND', '挂起', 'SUSPENDED', [SOAllocateStatus.ALLOCATED], [SOAllocateStatus.CANCELLED]);
  /** 撤回/释放：ALLOCATED|SUSPENDED → AVAILABLE */
  static readonly RELEASE = new SOAllocateAction('RELEASE', '释放', 'AVAILABLE', [SOAllocateStatus.ALLOCATED, SOAllocateStatus.SUSPENDED], [SOAllocateStatus.CANCELLED]);
}

/**
 * SO 操作枚举（船东谈判动作；gate 在 status）
 */
export class SOAction extends BaseEnumClass<SOAction> {
  constructor(
    public readonly code: SOActionCode,
    public readonly title: string,
    public readonly targetStatus: SOStatusCode | '' = '',
    public readonly allowedStatuses: SOStatus[] = [],
    public readonly disallowedStatuses: SOStatus[] = []
  ) { super(); }

  /**
   * 检查某个状态是否允许执行当前操作
   */
  canExecute(status: string): boolean {
    const statusObj = SOStatus.fromCode<SOStatus>(status);
    if (!statusObj) return false;
    if (this.disallowedStatuses.includes(statusObj)) return false;
    if (this.allowedStatuses.length === 0) return true;
    return this.allowedStatuses.includes(statusObj);
  }

  static readonly VIEW = new SOAction('VIEW', '查看', '');
  static readonly MODIFY = new SOAction('MODIFY', '编辑', 'MODIFIED', [], [SOStatus.CANCELLED, SOStatus.EXPIRED]);
  static readonly ACK = new SOAction('ACK', '标记已受理', 'ACKED', [SOStatus.ENTERED, SOStatus.MODIFIED]);
  static readonly CONFIRM = new SOAction('CONFIRM', '标记订舱成功', 'CONFIRMED', [SOStatus.ENTERED, SOStatus.ACKED]);
  static readonly CANCEL = new SOAction('CANCEL', '取消', 'CANCELLED', [], [SOStatus.CANCELLED, SOStatus.EXPIRED]);
  static readonly RECOVER = new SOAction('RECOVER', '复原', 'MODIFIED', [SOStatus.CANCELLED]);
  static readonly PUBLISH = new SOAction('PUBLISH', '发布', '', [SOStatus.CONFIRMED, SOStatus.MODIFIED]);
  static readonly UNPUBLISH = new SOAction('UNPUBLISH', '撤回', '', [SOStatus.CONFIRMED, SOStatus.MODIFIED]);
  static readonly MARK_CABIN_TYPE = new SOAction('MARK_CABIN_TYPE', '标记舱保类型', 'MODIFIED');
  /** 确认邮件来源识别正确：清空 email_source_id 并记历史，不参与状态流转 */
  static readonly VERIFY_EMAIL_SOURCE = new SOAction('VERIFY_EMAIL_SOURCE', '确认邮件识别正确', '');
  /**
   * 真删除（物理删除，含 allocation / history 子表，不可恢复）
   * 不参与状态流转，任何状态均可执行；故 allowedStatuses / disallowedStatuses 均为空。
   */
  static readonly DELETE = new SOAction('DELETE', '删除', '');
}


/**
 * 用类定义的方式来声明版本类型枚举
 *
 * @version 1.0.0
 */
export class BookingVersionType extends BaseEnumClass<BookingVersionType> {
  constructor(
    public readonly code: string,
    public readonly title: string
  ) { super(); }

  // 枚举值定义
  static readonly INIT = new BookingVersionType("INIT", "初始版本");
  static readonly CHANGE = new BookingVersionType("CHANGE", "变更版本");
}

/**
 * 用类定义的方式来声明版本状态枚举
 *
 * @version 1.0.0
 */
export class BookingVersionStatus extends BaseEnumClass<BookingVersionStatus> {
  constructor(
    public readonly code: string,
    public readonly title: string
  ) { super(); }

  // 枚举值定义
  static readonly DRAFT = new BookingVersionStatus("DRAFT", "草稿"); // 暂不使用
  static readonly PUBLISHED = new BookingVersionStatus("PUBLISHED", "已发布");
  static readonly ARCHIVED = new BookingVersionStatus("ARCHIVED", "已归档"); // 暂不使用
}


/**
 * 用类定义的方式来声明邮件状态枚举
 *
 * @version 1.0.0
 */
export class EmailStatus extends BaseEnumClass<EmailStatus> {
  constructor(
    public readonly code: string,
    public readonly category: string,
    public readonly title: string
  ) { super(); }

  // 枚举值定义
  static readonly DATABASE_STORAGE_NORMAL = new EmailStatus("00", "落库阶段，正常情况", "新落库，待识别");
  static readonly RECOGNITION_NORMAL = new EmailStatus("01", "识别阶段，正常情况", "已识别，正常解析");
  static readonly RECOGNITION_NO_ATTACHMENT = new EmailStatus("02", "识别阶段，附件异常", "已识别，没有附件");
  static readonly RECOGNITION_NO_BOOKING_ORDER = new EmailStatus("03", "识别阶段，附件异常", "已识别，没有托单附件");
  static readonly RECOGNITION_MULTIPLE_BOOKING_ORDERS = new EmailStatus("04", "识别阶段，附件异常", "已识别，多个托单附件");
  static readonly RECOGNITION_INFO_INCONSISTENT = new EmailStatus("05", "识别阶段，信息异常", "已识别，主题/正文/附件信息不一致");
  static readonly RECOGNITION_MISSING_ELECTRONIC_SEAL = new EmailStatus("06", "识别阶段，信息异常", "已识别，缺少电子章");
  static readonly RECOGNITION_IRRELEVANT_EMAIL = new EmailStatus("07", "识别阶段，信息异常", "已识别，是无关邮件");
  static readonly RECOGNITION_NO_BOOKING_ORDER_INFO = new EmailStatus("31", "识别阶段，附件异常", "已识别，托单附件未识别到信息");
  static readonly RECOGNITION_INTERNAL_ERROR = new EmailStatus("41", "识别阶段，其他异常", "已识别，其他异常");
  static readonly RECOGNITION_VALUE_INVALID = new EmailStatus("21", "识别阶段，信息异常", "已识别，信息与主数据不一致");
  static readonly RECOGNITION_SPECIAL_CABIN_TYPE = new EmailStatus("22", "识别阶段，信息异常", "已识别，特殊柜型");
  static readonly RECOGNITION_NEED_MODIFY_BOOKING = new EmailStatus("08", "识别阶段，订单变动", "已识别，需修改订舱");
  static readonly RECOGNITION_NEED_CANCEL_BOOKING = new EmailStatus("09", "识别阶段，订单变动", "已识别，需取消订舱");
  static readonly RECOGNITION_NEED_MERGE_BOOKING = new EmailStatus("10", "识别阶段，订单变动", "已识别，需合并订舱");
  static readonly PROCESS_AS_VERIFY_REJECT_EMAIL = new EmailStatus("11", "处理阶段，审核未通过", "已处理，是审核未通过邮件");
  static readonly PROCESSING_CREATED_BOOKING_ORDER = new EmailStatus("12", "处理阶段，处理托单", "已处理，已创建托单");
  static readonly PROCESSING_MODIFIED_BOOKING_ORDER = new EmailStatus("13", "处理阶段，处理托单", "已处理，已修改托单");
  static readonly PROCESSING_DELETED_BOOKING_ORDER = new EmailStatus("14", "处理阶段，处理托单", "已处理，已取消托单");
  static readonly PROCESSING_MERGED_BOOKING_ORDER = new EmailStatus("15", "处理阶段，处理托单", "已处理，已合并托单");
  static readonly PROCESSING_RECOGNITION_REPARSE = new EmailStatus("50", "识别阶段，正常情况", " 已触发，重新识别");
}

/**
 * 用类定义的方式来声明邮件操作枚举
 *
 * @version 1.0.0
 */
export class EmailProcess extends BaseEnumClass<EmailProcess> {
  constructor(
    public readonly code: string,
    public readonly title: string,
    public readonly status: string
  ) { super(); }

  // 枚举值定义
  static readonly STORE = new EmailProcess("STORE", "落库", EmailStatus.DATABASE_STORAGE_NORMAL.code);
  static readonly RECOG = new EmailProcess("RECOG", "识别", EmailStatus.RECOGNITION_NORMAL.code);
  static readonly PROCESS_AS_VERIFY_REJECT_EMAIL = new EmailProcess("PROCESS_AS_VERIFY_REJECT_EMAIL", "处理_审核未通过邮件", EmailStatus.PROCESS_AS_VERIFY_REJECT_EMAIL.code);
  static readonly PROCESS_CREATE_BOOKING = new EmailProcess("PROCESS_CREATE_BOOKING", "处理_创建托单", EmailStatus.PROCESSING_CREATED_BOOKING_ORDER.code);
  static readonly PROCESS_AMEND_BOOKING = new EmailProcess("PROCESS_AMEND_BOOKING", "处理_修改托单", EmailStatus.PROCESSING_MODIFIED_BOOKING_ORDER.code);
  static readonly PROCESS_MERGE_BOOKING = new EmailProcess("PROCESS_MERGE_BOOKING", "处理_合并托单", EmailStatus.PROCESSING_MERGED_BOOKING_ORDER.code);
  static readonly PROCESS_CANCEL_BOOKING = new EmailProcess("PROCESS_CANCEL_BOOKING", "处理_取消托单", EmailStatus.PROCESSING_DELETED_BOOKING_ORDER.code);
}

/**
 * 通用状态枚举类：如 用户状态、角色状态
 *
 * @version 1.0.0
 */
export class CommonStatusType extends BaseEnumClass<CommonStatusType> {
  constructor(
    public readonly code: string,
    public readonly title: string
  ) { super(); }

  // 枚举值定义
  static readonly ON = new CommonStatusType("ON", "启用");
  static readonly OFF = new CommonStatusType("OFF", "停用");
  static readonly PENDING = new CommonStatusType("PENDING", "待激活");
}

/**
 * 构建部门树
 *
 * @param departments 部门数据
 * @param showOnlyDepartment 是否只显示部门
 * @returns 部门树
 * @version 1.0.0
 */
export function buildDepartmentTree(departments: any[] | undefined, showOnlyDepartment: boolean): Department[] {
  if (showOnlyDepartment) {
    return [{
      name: '部门',
      code: 'department',
      dropdownMenu: false,
      selectable: false,
      children: departments as Department[]
    }];
  }
  return [{
    code: 'status',
    name: '状态',
    selectable: false,
    dropdownMenu: false,
    children: [{
      code: 'PENDING',
      name: '待授权',
      dropdownMenu: false
    }] as Department[]
  }, {
    name: '部门',
    code: 'department',
    dropdownMenu: false,
    selectable: false,
    children: departments as Department[]
  }];
}

export const AppConfig: Record<string, any> = {
  debounceTime: 1000, // 防抖时间
  // 权限常量定义
  permissions: {
    dept: {
      // 当前部门订舱管理权限
      BOOKING_MANAGER_CURRENT_DEPT: 'dept:booking-mgr-curr-dept',
      // 子部门订舱管理权限
      BOOKING_MANAGER_SUB_DEPT: 'dept:booking-mgr-sub-dept',
    },
    supervisor: {
      // 港口权限主管（通过用户身上的港口权限匹配数据）
      EMAIL: 'supervisor:email',
      BOOKING: 'supervisor:booking',
      SO: 'supervisor:so',
    },
    owner: {
      OPERATOR: 'owner:operator',
      SALES: 'owner:sales',
    },
    button: {
      BOOKING_CHANGE_PORT_GROUP: 'btn:booking>change_port_group',
      // 取消托单时允许选择「不发送邮件」的权限；无此权限只能发送邮件
      BOOKING_CANCEL_WITHOUT_EMAIL: 'btn:booking>cancel-without-email',
      // 维护托单相关方（无权限时相关方弹窗为只读查看）
      BOOKING_STAKEHOLDER_MAINTAIN: 'btn:booking>stakeholder_maintain',
      // 整柜基准运价导入：删除任务文件（无权限时不展示删除入口）
      FCL_PRICE_IMPORT_DELETE_FILE: 'btn:fcl-price-import>delete-file',
    },
  },
  booking: {
    importSetting: {
      defaultBatchSize: 2000,
      requestDelay: 1000,
    },
    // SO 自动联动开关：默认关闭，开启后 booking 编辑时柜量/航线/柜型变化会自动释放不匹配的 SO 分配
    soAutoLinkEnabled: false,
  },
  logging: {
    showTrace: false, // 控制是否显示console.trace
    traceModules: [// 控制哪些模块显示trace
      'redis',
      'middleware',
      'cargoplus',
    ],
  },
  publicPaths: [
    '/error', // 错误页面
    '/login', // 登录页面
    '/api/v1/auth/login', // 登录接口
    '/api/v1/auth/refresh', // 刷新token接口
    '/api/open/cargoplus/login', // 第三方对接-宜运-登录接口
    '/api/open/bookings/expired', // 第三方对接-订舱管理-处理过期托单接口
    '/api/v1/auth/login-with-token', // 第三方对接-登录 token 获取接口
    // '/api/test/redis', // 测试redis接口
    '/api/v1/masterdata/AppInfo/record', // 系统配置项获取接口
    '/api/proxy/', // 代理接口
  ],
  openApiPath: [
    '/api/open/yida/', // 第三方对接-宜搭
    '/api/open/so_email/', // 第三方对接-SO邮件处理(mail-crawler action)
  ],
  info: {
    tenantCode: "HUNICORN",
    title: "Booking System",
    title_cn: "订舱管理系统",
    description: "Design by ReknowInfo",
    slogan: "Together We Deliver",
    slogan_sub: "HUNICORN, YOUR LOGISTICS PARTNER IN CHINA.",
  },
  // API 配置
  api: {
    apiRoot: process.env.NEXT_PUBLIC_API_ROOT || 'http://localhost:8000',
  },
  headers: {
    userId: 'x-user-id',
    userName: 'x-user-name',
    userEmail: 'x-user-email',
    userAccount: 'x-user-account',
    authToken: 'x-auth-token',
    apiToken: 'x-api-token',
    refreshToken: 'x-refresh-token',
    useCache: 'x-use-cache',
    requestId: 'x-request-id',
    requestFrom: 'x-request-from',
    skipTokenVerify: 'x-skip-token-verify',
    redirectToLogin: 'x-redirect-to-login',
    useApiTip: 'x-use-api-tip', // 使用 api-tip 提示
  },
  thirdparty: {
    tokenTTl: 600, // 10分钟
    loginUrl: `${process.env.NEXT_PUBLIC_API_ROOT}/login`, // 登录接口
  },
  tableConfig: {
    pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (total: number) => `共 ${total} 条`, }
  },
  masterdata: {
    defaultVersion: '1.0',
    specialCode: {
      EnumDict: "EnumDict",
      EntityDict: "EntityDict",
      FreightCarrier: "FreightCarrier",
      FreightPortGroup: "FreightPortGroup",
      FreightCarrierPortGroup: "FreightCarrierPortGroup",
      FreightPortGroupPol: "FreightPortGroupPol",
      FreightPortGroupPod: "FreightPortGroupPod",
    },
    cacheRecords: ['MenuItems', 'MenuItem', 'PermissionTypes', 'TimeRangeOptions', 'AppInfo', 'ImportConfig', 'CardFields', 'TableColumns', 'TimeSearchFields'],
    // 定义需要权限校验的主数据
    permitRecords: ["MenuItems", "MenuItem"]
  },
  cacheKeys: {
    masterdataAll: (tenantCode: string, masterdataCode: string) => `${tenantCode}:masterdata:${masterdataCode}:*`,
    masterdataDefinition: (tenantCode: string, masterdataCode: string, version: string) => `${tenantCode}:masterdata:${masterdataCode}:definition:${version}`,
    masterdataRecordByIdContent: (tenantCode: string, masterdataCode: string, version: string, idContent: string) => `${tenantCode}:masterdata:${masterdataCode}:records:${version}:${idContent}`,
    masterdataRecords: (tenantCode: string, masterdataCode: string, version: string) => `${tenantCode}:masterdata:${masterdataCode}:records:${version}`,
    masterdataOptions: (tenantCode: string, masterdataCode: string, version: string) => `${tenantCode}:masterdata:${masterdataCode}:options:${version}`,
    userAll: (tenantCode: string, account: string) => `${tenantCode}:user:${account}:*`,
    userIdAll: (tenantCode: string, userId: number) => `${tenantCode}:user:${userId}:*`,
    portAuth: (tenantCode: string, userId: number) => `${tenantCode}:user:${userId}:port:auth`,
    emailAuth: (tenantCode: string, userId: number) => `${tenantCode}:user:${userId}:email:auth`,
    bookingAuth: (tenantCode: string, userId: number) => `${tenantCode}:user:${userId}:booking:auth`,
    userPermissions: (tenantCode: string, account: string) => `${tenantCode}:user:${account}:permissions`,
    userPathPermissions: (tenantCode: string, account: string) => `${tenantCode}:user:${account}:path_permission`,
    userDataPermissions: (tenantCode: string, account: string) => `${tenantCode}:user:${account}:data_permissions`,
    userByAccount: (tenantCode: string, account: string) => `${tenantCode}:user:${account}:info`,
    userThirdPartyLogin: (tenantCode: string, tokenKey: string) => `${tenantCode}:user:third_party_login:${tokenKey}`,
    roleHashByCode: (tenantCode: string) => `${tenantCode}:role:hash_by_code`,
    freightPorts: (tenantCode: string) => `${tenantCode}:freight:ports`,
    freightPortAlias: (tenantCode: string, carrierCode: string) => `${tenantCode}:freight:port-alias:${carrierCode}`,
    freightPortsCombined: (tenantCode: string, carrierCode?: string) => `${tenantCode}:freight:ports:combined:${carrierCode || ''}`,
    freightPortsCombinedNoGroupFilter: (tenantCode: string, carrierCode: string) => `${tenantCode}:freight:ports:combined_no_group_filter:${carrierCode}`,
    freightCarriers: (tenantCode: string) => `${tenantCode}:freight:carriers`,
    freightPortGroups: (tenantCode: string, carrierCode: string) => `${tenantCode}:freight:port-groups:${carrierCode}`,
  },
  copyIgnoreFields: ['bookingNo', 'bookingTime', 'containerInfos', 'containerCount', 'containerTEU', 'mergeSO', 'packages', 'gross', 'volume', 'etd'],
  dataPermissionFieldSequence: {
    'booking': ['carrier', 'pol', 'pod'],
  },
  /**
   * SO 邮件处理配置
   * 事件类型、状态、动作映射、终态/可重试态等业务规则集中配置，便于按承运商覆盖。
   */
  soEmail: {
    eventTypeCodes: ['CONFIRMATION', 'CANCELLATION', 'AMENDMENT', 'ACKNOWLEDGEMENT', 'UNKNOWN'] as SOEmailEventTypeCode[],
    statusCodes: [
      'PENDING', 'PARSE_SUCCESS', 'PARSE_FAILED', 'SO_MATCHED', 'SO_UNMATCHED',
      'ACTION_SUCCESS', 'ACTION_FAILED', 'SYSTEM_ERROR', 'IGNORED',
    ] as SOEmailStatusCode[],
    // 动作说明：
    // UPDATE — 只更新已匹配到的 SO；匹配不到时落 SO_UNMATCHED 拦截，走人工处理，不会自动创建
    // UPSERT — 匹配到则更新，匹配不到则自动创建新 SO
    defaultActionMap: {
      CONFIRMATION: 'UPDATE',
      ACKNOWLEDGEMENT: 'UPDATE',
      AMENDMENT: 'UPDATE',
      CANCELLATION: 'CANCEL',
      UNKNOWN: 'IGNORED',
    } as Record<SOEmailEventTypeCode, SOEmailAction>,
    carrierActionOverrides: {} as Record<string, Partial<Record<SOEmailEventTypeCode, SOEmailAction>>>,
    eventTypeToSOStatusMap: {
      CONFIRMATION: 'CONFIRMED',
      ACKNOWLEDGEMENT: 'ACKED',
      AMENDMENT: 'MODIFIED',
      CANCELLATION: 'CANCELLED',
    } as Partial<Record<SOEmailEventTypeCode, SOStatusCode>>,
    terminalStatuses: ['ACTION_SUCCESS', 'IGNORED'] as SOEmailStatusCode[],
    retryableStatuses: [
      'PARSE_FAILED', 'SO_UNMATCHED', 'SO_MATCHED', 'ACTION_FAILED', 'SYSTEM_ERROR',
    ] as SOEmailStatusCode[],
  },
  /**
   * 外部域名接口配置
   * 用于服务器端代理功能，可以配置多个外部服务的基础URL
   */
  externalApis: {
    // 示例配置：可以根据实际需要添加更多外部服务
    'cargo-master': {
      base_url: process.env.EXTERNAL_API_CARGO_MASTER_BASE_URL || 'http://localhost',
      login_api: {
        url: process.env.EXTERNAL_API_CARGO_MASTER_LOGIN_URL || '/api/v1/adm/thirdparty/login',
        method: process.env.EXTERNAL_API_CARGO_MASTER_LOGIN_METHOD || 'POST',
        headers: {
          'X-Tenant-Code': process.env.EXTERNAL_API_CARGO_MASTER_TENANT_CODE || 'HUNICORN',
          'X-Thirdparty-Code': process.env.EXTERNAL_API_CARGO_MASTER_THIRDPARTY_CODE || 'booking',
        },
        requestParam: {
          token: process.env.EXTERNAL_API_CARGO_MASTER_LOGIN_TOKEN || 'wx49d77be7f8e59b65'
        }
      }
    },
    // 可以添加更多外部服务配置
    // service1: process.env.EXTERNAL_API_SERVICE1_URL || '',
    // service2: process.env.EXTERNAL_API_SERVICE2_URL || '',
  },
  portColumns: [
    {
      title: "区域",
      dataIndex: "region",
      key: "region",
      width: 140,
      hasSearchInColumn: true,
      searchOptions: {
        widget: 'input',
        props: {
          placeholder: '搜索区域'
        },
      },
      $editable: true
    },
    {
      title: "国家",
      dataIndex: "country",
      key: "country",
      width: 140,
      hasSearchInColumn: true,
      searchOptions: {
        widget: "input",
        placeholder: '搜索国家'
      },
      $editable: true
    },
    {
      title: "城市",
      dataIndex: "city",
      key: "city",
      width: 140,
      hasSearchInColumn: true,
      searchOptions: {
        widget: 'input',
        props: {
          placeholder: '搜索城市'
        }
      },
      $editable: true
    },
    {
      title: "港口",
      dataIndex: "name",
      key: "name",
      width: 180,
      hasSearchInColumn: true,
      searchOptions: {
        widget: 'input',
        props: {
          placeholder: '搜索港口'
        }
      },
      $editable: true
    },
    {
      title: "五字码",
      dataIndex: "un_locode",
      key: "un_locode",
      width: 140,
      hasSearchInColumn: true,
      searchOptions: {
        widget: 'input',
        props: {
          placeholder: '搜索五字码'
        }
      },
      $editable: true
    },
    {
      title: "港口编码",
      dataIndex: "code",
      key: "code",
      minWidth: 120,
      hasSearchInColumn: true,
      searchOptions: {
        widget: 'input',
        props: {
          placeholder: '搜索港口编码'
        }
      },
      $editable: "{{ record.id?.toString().startsWith('new_') }}"
      // editable: (_: any, record: Thing) => record.id?.toString().startsWith('new_'),
    },
  ],
  // AI 大模型配置
  ai: {
    // LLM API 配置（用于自然语言转 SQL）
    llm: {
      apiKey: process.env.LLM_API_KEY || '',
      baseUrl: process.env.LLM_BASE_URL || 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    },
    // AI 聊天模型配置
    chat: {
      model: process.env.AI_CHAT_MODEL || 'qwen3.5-122b-a10b',
      apiKey: process.env.AI_CHAT_API_KEY || '',
      baseUrl: process.env.AI_CHAT_BASE_URL || 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    },
  }
};

export const approvalStatusMap = {
  [BookingProcess.APPROVE_PASS.code]: 'Y',
  [BookingProcess.APPROVE_REJECT.code]: 'N',
  [BookingProcess.APPROVE_PENDING.code]: 'P',
};

/**
 * SO 邮件处理状态枚举（按管线阶段建模）
 */
export class SOEmailStatus extends BaseEnumClass<SOEmailStatus> {
  constructor(
    public readonly code: SOEmailStatusCode,
    public readonly title: string,
    public readonly color: string
  ) { super(); }

  static readonly PENDING = new SOEmailStatus('PENDING', '待处理', 'default');
  static readonly PARSE_SUCCESS = new SOEmailStatus('PARSE_SUCCESS', '已解析', 'blue');
  static readonly PARSE_FAILED = new SOEmailStatus('PARSE_FAILED', '解析失败', 'error');
  static readonly SO_MATCHED = new SOEmailStatus('SO_MATCHED', '已匹配', 'cyan');
  static readonly SO_UNMATCHED = new SOEmailStatus('SO_UNMATCHED', '未匹配', 'warning');
  static readonly ACTION_SUCCESS = new SOEmailStatus('ACTION_SUCCESS', '处理成功', 'success');
  static readonly ACTION_FAILED = new SOEmailStatus('ACTION_FAILED', '处理失败', 'error');
  static readonly SYSTEM_ERROR = new SOEmailStatus('SYSTEM_ERROR', '系统异常', 'volcano');
  static readonly IGNORED = new SOEmailStatus('IGNORED', '已忽略', 'default');
}

/**
 * SO 邮件事件类型枚举
 */
export class SOEmailEventType extends BaseEnumClass<SOEmailEventType> {
  constructor(
    public readonly code: SOEmailEventTypeCode,
    public readonly title: string,
    public readonly color: string
  ) { super(); }

  static readonly CONFIRMATION = new SOEmailEventType('CONFIRMATION', '确认', 'green');
  static readonly CANCELLATION = new SOEmailEventType('CANCELLATION', '取消', 'red');
  static readonly ACKNOWLEDGEMENT = new SOEmailEventType('ACKNOWLEDGEMENT', '回执', 'blue');
  static readonly AMENDMENT = new SOEmailEventType('AMENDMENT', '变更', 'orange');
}
